import { type FC } from "react";
import { cn } from "@/components/podkit/lib/cn";

type FlexTruncatedTextProps = {
    text: string;
    separator?: string;
    className?: string;
};

/**
 * Flexbox-based truncation that keeps first and last segments visible
 * while allowing middle segments to shrink with ellipsis
 */
export const FlexTruncatedText: FC<FlexTruncatedTextProps> = ({ text, separator = "/", className }) => {
    const parts = text.split(separator);

    // For single segment, just show it
    if (parts.length === 1) {
        return (
            <span className={cn(className)} aria-label={text}>
                {text}
            </span>
        );
    }

    // For two segments, optimize DOM structure with just two spans
    if (parts.length === 2) {
        return (
            <div className={cn("flex overflow-hidden whitespace-nowrap", className)} aria-label={text}>
                <span className="flex-shrink-0">
                    {parts[0]}
                    {separator}
                </span>
                <span className="min-w-0 flex-shrink overflow-hidden text-ellipsis">{parts[1]}</span>
            </div>
        );
    }

    // For 3+ segments: first + truncatable middle + last
    const first = parts[0];
    const last = parts[parts.length - 1];
    const middleParts = parts.slice(1, -1);

    return (
        <div className={cn("flex overflow-hidden whitespace-nowrap", className)} aria-label={text}>
            <span className="flex-shrink-0">{first}</span>
            <span className="flex-shrink-0">{separator}</span>
            <span className="min-w-0 flex-shrink overflow-hidden text-ellipsis">
                {middleParts.map((part, index) => (
                    <span key={index}>
                        {part}
                        {index < middleParts.length - 1 && separator}
                    </span>
                ))}
            </span>
            <span className="flex-shrink-0">{separator}</span>
            <span className="flex-shrink-0">{last}</span>
        </div>
    );
};
